import {
  Box,
  buildPath,
  buildTemplatePath,
  Button,
  Card,
  CloseButton,
  formatPhoneNumber,
  Grid,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  UpdateButton,
  useElementBreakpoints,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  GetCreateTextingConversationsResponseDto,
  LkPersonTypeId,
  PersonDto,
  SaveCreateTextingConversationsRequestDto,
  SaveCreateTextingOptInDto,
  Student,
  TextingOptInDto,
  TextingRecipientDto,
} from "generated/openApi";
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useRouter } from "hooks";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { SelectedPersons } from "./SelectedPersons";
import { Input, Select, StudentCustomIdsPaster, SubmitButton } from "components";
import { setAppToast } from "redux/reducers/appSlice";
import produce from "immer";
import { pillToggleGroupStyles } from "pages/HomePage/styles/segmentedControlStyles";

const routeName = "createTextingConversations";

export const createTextingConversationsPageRoute: PageRoute = {
  handle: "Send Text Messages",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type CreateTextingConversationsPageState = {
  personTypeId: LkPersonTypeId;
  personIds: number[];
};

type ConversationTabs = 'students' | 'parents';

export const CreateTextingConversationsPage = () => {
  const dispatch = useDispatch();
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<CreateTextingConversationsPageState>(state);
  const [createDto, setCreateDto] = useState<GetCreateTextingConversationsResponseDto>();
  const properties = createDto?.savingRequestProperties ?? [];
  const [activeTab, setActiveTab] = useState<ConversationTabs>(
    stateParam.personTypeId === LkPersonTypeId.Parent ? "parents" : "students"
  );
  const [studentPersons, setStudentPersons] = useState<PersonDto[]>([]);
  const [parentPersons, setParentPersons] = useState<PersonDto[]>([]);
  const persons = activeTab === "parents" ? parentPersons : studentPersons;

  const [saveRequest, setSaveRequest] = useState<SaveCreateTextingConversationsRequestDto>({
    personTypeId: stateParam.personTypeId,
  } as SaveCreateTextingConversationsRequestDto);

  // const [resource, setResource] = useState<CreateTextingConversationsResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({
    trackedItems: [saveRequest, studentPersons, parentPersons],
  });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  // Add new student
  const [optInStudentNumbers, setOptInStudentNumbers] = useState<TextingOptInDto[]>([]);
  const [optInParentNumbers, setOptInParentNumbers] = useState<TextingOptInDto[]>([]);

  const [optInNumbers, setOptInNumbers] = useState<TextingOptInDto[]>([]);
  const [selectedOptInNumber, setSelectedOptInNumber] = useState<TextingOptInDto>();

  const canSave = unsavedChanges && persons && persons?.length > 0;

  const isParentTab = activeTab === "parents";
  const studentServiceTypes = createDto?.studentServiceTypes?.filter((x) => x.isForParent === isParentTab) || [];
  const studentServiceSubTypes =
    createDto?.studentServiceSubTypes?.filter((x) => x.serviceTypeId === saveRequest.studentServiceTypeId) || [];

  useEffect(() => {
    if (!saveRequest.studentServiceTypeId) return;

    const selectedServiceType = createDto?.studentServiceTypes?.find((x) => x.id === saveRequest.studentServiceTypeId);
    if (selectedServiceType && selectedServiceType.isForParent !== isParentTab) {
      setSaveRequest((prev) => ({
        ...prev,
        studentServiceTypeId: undefined,
        studentServiceSubTypeId: undefined,
      }));
    }
  }, [activeTab, createDto?.studentServiceTypes, saveRequest.studentServiceTypeId]);

const handleViewChange = (_: React.MouseEvent<HTMLElement>, newTab: ConversationTabs | null) => {
    if (!newTab || newTab === activeTab) return;

    setActiveTab(newTab);
    setSelectedOptInNumber(undefined); // Clear any staging input values when switching tabs
    setSaveRequest((prev) => ({
      ...prev,
      personTypeId: newTab === "parents" ? LkPersonTypeId.Parent : LkPersonTypeId.Student,
    }));
  };

  // fetch initial
  useEffect(() => {
    ApiService.textingConversations
      .getCreateConversations({
        personTypeId: stateParam.personTypeId,
        personIds: stateParam.personIds,
      })
      .then((response) => {
        setCreateDto(response);
        setStudentPersons(response.persons?.filter((person) => !person.isParent) ?? []);
        setParentPersons(response.persons?.filter((person) => person.isParent) ?? []);
      });

    ApiService.textingConversations
      .getOptedInStudentNumbers()
      .then((response) => {
        setOptInStudentNumbers(response);
      });

    ApiService.textingConversations
      .getOptedInParentNumbers()
      .then((response) => {
        setOptInParentNumbers(response);
      });
  }, []);

const save = (request: SaveCreateTextingConversationsRequestDto) => {
  setValidationErrors({});
  setSelectedItemsErrors([]);

  // Map your state persons to match the new C# DTO structure
  const recipients: TextingRecipientDto[] = persons?.map((p) => ({
    id: p.id,
    phoneNumber: p.cellPhone,
    isParent: !!p.isParent
  })) ?? [];

  const savingPayload: SaveCreateTextingConversationsRequestDto = {
    ...request,
    recipients: recipients, // Swapped personIds for recipients
  };

  ApiService.textingConversations
    .saveCreateConversations(savingPayload)
    .then((response) => {
      const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
        response as unknown as BulkSecondLevelValidation;

      if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
        setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        dispatch(setAppToast(newToast("error")));
      } else {
        resetUnsavedChanges([saveRequest, studentPersons, parentPersons]);
        listContext.loadList?.();
        listContext.clearSelectedIds?.();
        dispatch(setAppToast(newToast("success")));
      }
    })
    .catch((ex) => {
      dispatch(setAppToast(newToast("error")));
      setValidationErrors(ex.data);
    });
};

  const sendOptIns = (request: SaveCreateTextingOptInDto) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    var savingPayload: SaveCreateTextingOptInDto = {
      ...request,
      personIds: persons?.filter(p => p.cellPhone && !optInStudentNumbers.some(n => n.phoneNumber == p.cellPhone)).map((x) => x.id) ?? [],
    };

    ApiService.textingConversations
      .saveCreateConversationsOptIn(savingPayload)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
          dispatch(setAppToast(newToast("error")));
        } else {
          resetUnsavedChanges([saveRequest, studentPersons, parentPersons]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
          dispatch(setAppToast(newToast("success")));
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const header = (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="inherit">{createTextingConversationsPageRoute.handle}</Typography>

      <Stack direction="row" alignItems="center" spacing="sm">
        <CloseButton confirm={unsavedChanges} />

        {/* {persons.some(p => p.cellPhone && !optInNumbers.some(n => n.phoneNumber == p.cellPhone)) && (<UpdateButton label="Send Opt-Ins" onClick={() => sendOptIns(saveRequest)} />)} */}

        <SubmitButton label={`Send: ${persons?.length}`} onClick={() => save(saveRequest)} disabled={!canSave} />
      </Stack>
    </Stack>
  );

  function messageContainer() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("messageBody");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        autoFocus
        inputProps={{ tabIndex: 1 }}
        modelProperty={property}
        errors={validationErrors[propertyName]}
        multiline
        placeholder="Message"
        value={saveRequest.messageBody}
        onChange={(e) => setSaveRequest({ ...saveRequest, messageBody: e.target.value })}
      />
    );
  }

  function studentServiceTypeField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("studentServiceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        inputProps={{ tabIndex: 2 }}
        errors={validationErrors[propertyName]}
        value={studentServiceTypes.find((x) => x.id === saveRequest.studentServiceTypeId)}
        options={studentServiceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          setSaveRequest({
            ...saveRequest,
            studentServiceTypeId: value?.id ?? undefined,
            studentServiceSubTypeId: undefined,
          })
        }
      />
    );
  }

  function studentServiceSubTypeField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("studentServiceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        inputProps={{ tabIndex: 3 }}
        errors={validationErrors[propertyName]}
        value={studentServiceSubTypes?.find((x) => x.id === saveRequest.studentServiceSubTypeId)}
        options={studentServiceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          setSaveRequest({
            ...saveRequest,
            studentServiceSubTypeId: value?.id ?? undefined,
          })
        }
      />
    );
  }

  function addPersonField() {
    return (
      <Select
        // modelProperty={property}
        // errors={props.validationErrors[propertyName]}
        inputProps={{ tabIndex: 4 }}
        value={optInStudentNumbers?.find((x) => x.studentId === selectedOptInNumber?.studentId)}
        options={optInStudentNumbers}
        getOptionLabel={(option) =>
          `${option.studentLastName}, ${option.studentFirstName} (${formatPhoneNumber(option.phoneNumber)})`
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack>
              <Stack direction="row">
                <Typography>{option.studentLastName}, {option.studentFirstName}</Typography>
              </Stack>

              <Typography variant="caption">{formatPhoneNumber(option.phoneNumber)}</Typography>
            </Stack>
          </Box>
        )}
        onChange={(e, value) => {
          setSelectedOptInNumber(value)
        }
        }
      />
    );
  }

  function addParentField() {
    return (
      <Select
        inputProps={{ tabIndex: 4 }}
        value={optInParentNumbers?.find((x) => x.parentId === selectedOptInNumber?.parentId)}
        options={optInParentNumbers}
        getOptionLabel={(option) =>
          `${option.parentLastName}, ${option.parentFirstName} (${formatPhoneNumber(option.phoneNumber)})`
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack spacing="sm">
              {/* Parent Name */}
              <Stack direction="row">
                <Typography sx={{ fontWeight: 'medium' }}>
                  {option.parentLastName}, {option.parentFirstName}
                </Typography>
              </Stack>

              {/* Students List */}
              <Stack direction="row">
                {option.students && option.students.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Students:{' '}
                    {option.students
                      .map((student) => student.studentInfo)
                      .join(', ')}
                  </Typography>
                )}
              </Stack>

              {/* Phone Number */}
              <Typography variant="caption" color="text.secondary">
                {formatPhoneNumber(option.phoneNumber)}
              </Typography>
            </Stack>
          </Box>
        )}
        onChange={(e, value) => setSelectedOptInNumber(value)}
      />
    );
  }

  function staffServiceTypeField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("staffServiceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={validationErrors[propertyName]}
        value={createDto?.staffServiceTypes?.find((x) => x.id === saveRequest.staffServiceTypeId)}
        options={createDto?.staffServiceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          setSaveRequest({
            ...saveRequest,
            staffServiceTypeId: value?.id ?? undefined,
          })
        }
      />
    );
  }

  const toStudentPerson = (student: TextingOptInDto): PersonDto => ({
    id: student.studentId || 0,
    personTypeId: Number(LkPersonTypeId.Student),
    firstName: student.studentFirstName,
    lastName: student.studentLastName,
    cellPhone: student.phoneNumber,
    identification: student.customId1,
    isParent: false,
  });

  const toStudentPersonFromStudent = (student: Student): PersonDto => ({
    id: student.id,
    personTypeId: Number(LkPersonTypeId.Student),
    firstName: student.firstName,
    lastName: student.lastName,
    cellPhone: student.cellPhone,
    identification: student.customId1,
    isParent: false,
  });

  const addStudents = (students: TextingOptInDto[]) => {
    setStudentPersons((prev) => {
      const updatedPersons = new Map(
        prev.map((person) => [`student-${person.id}`, person])
      );

      students.forEach((student) => {
        if (student.studentId) {
          updatedPersons.set(`student-${student.studentId}`, toStudentPerson(student));
        }
      });

      return Array.from(updatedPersons.values());
    });
  };

  const addStudentRecords = (students: Student[]) => {
    setStudentPersons((prev) => {
      const updatedPersons = new Map(
        prev.map((person) => [`student-${person.id}`, person])
      );

      students.forEach((student) => {
        updatedPersons.set(`student-${student.id}`, toStudentPersonFromStudent(student));
      });

      return Array.from(updatedPersons.values());
    });
  };

  const addStudentsByCustomIds = (customIdTypeId: number | undefined, customIds: string[]) => {
    if (!customIdTypeId || customIds.length === 0) return;

    ApiService.students
      .getByCustomIds({ customIdTypeId, customIds })
      .then((students) => addStudentRecords(students));
  };

const addSelectedStudent = () => {
    if (!selectedOptInNumber) return;

    addStudents([selectedOptInNumber]);
    setSelectedOptInNumber(undefined);
  };

const addSelectedParent = () => {
    if (!selectedOptInNumber) return;

    const newParent: PersonDto = {
      id: selectedOptInNumber.parentId || 0,
      personTypeId: Number(LkPersonTypeId.Parent),
      firstName: selectedOptInNumber.parentFirstName,
      lastName: selectedOptInNumber.parentLastName,
      cellPhone: selectedOptInNumber.phoneNumber,
      identification: selectedOptInNumber.parentId?.toString(),
      isParent: true,
    };

    setParentPersons((prev) => {
      const updatedPersons = new Map(prev.map((person) => [`parent-${person.id}`, person]));
      updatedPersons.set(`parent-${newParent.id}`, newParent);

      return Array.from(updatedPersons.values());
    });
    setSelectedOptInNumber(undefined);
  };

  const addStudentHeader = (
    <Stack direction="row" justifyContent={"space-between"} alignItems={"center"} spacing="sm">
      <Stack direction="row" spacing="md" alignItems="center">
        <Stack>Add Student</Stack>
      </Stack>

      <Stack direction="row" spacing="sm" alignItems="center">
        {selectedOptInNumber?.customId1 && (
          <Button onClick={() => addSelectedStudent()} variant="outlined" color="info">
            + Add
          </Button>
        )}
      </Stack>
    </Stack>
  );

  const addStudentParentHeader = (
    <Stack direction="row" justifyContent={"space-between"} alignItems={"center"} spacing="sm">
      <Stack direction="row" spacing="md" alignItems="center">
        <Stack>Add Parent</Stack>
      </Stack>

      <Stack direction="row" spacing="sm" alignItems="center">
        {selectedOptInNumber?.parentId && (
          <Button onClick={() => addSelectedParent()} variant="outlined" color="info">
            + Add
          </Button>
        )}
      </Stack>
    </Stack>
  );

  return (
    <>
      <Page isForm header={header}>
        <Stack spacing="lg" ref={breakpointsRef}>
          <Card header="Send Text to:">

            <Box>
              <Grid container spacing="sm">
                <Grid item xs={smallFieldBreakpoints}>
                  <ToggleButtonGroup
                    value={activeTab}
                    exclusive
                    onChange={handleViewChange}
                    sx={pillToggleGroupStyles}
                  >
                    <ToggleButton value="students">Students</ToggleButton>
                    <ToggleButton value="parents">Parents</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Box>
          </Card>

          <Card header="Compose Text Message">
            <Stack direction="column" spacing="md">
              {messageContainer()}

              <Box>
                <Grid container spacing="sm">
                  <Grid item xs={smallFieldBreakpoints}>
                    {studentServiceTypeField()}
                  </Grid>

                  {studentServiceSubTypes.length > 0 && (
                    <Grid item xs={smallFieldBreakpoints}>
                      {studentServiceSubTypeField()}
                    </Grid>
                  )}

                  {/* <Grid item xs={smallFieldBreakpoints}>
                  {staffServiceTypeField()}
                </Grid> */}
                </Grid>
              </Box>
            </Stack>
          </Card>

          {optInStudentNumbers && activeTab == "students" && (
            <Card header={addStudentHeader}>
              <Stack direction="column" spacing="md">
                <Grid container spacing="sm">
                  <Grid item xs={5}>
                    {addPersonField()}
                  </Grid>
                  <Grid item xs={12}>
                    <StudentCustomIdsPaster onAdd={addStudentsByCustomIds} />
                  </Grid>
                </Grid>
              </Stack>
            </Card>
          )}

          {optInParentNumbers && activeTab == "parents" && (
            <Card header={addStudentParentHeader}>
              <Stack direction="column" spacing="md">
                <Grid container spacing="sm">
                  <Grid item xs={5}>
                    {addParentField()}
                  </Grid>
                </Grid>
              </Stack>
            </Card>
          )}

          {(optInStudentNumbers != null && optInParentNumbers != null) && (<SelectedPersons
            optInNumbers={[...optInStudentNumbers, ...optInParentNumbers]}
            persons={new Map(persons?.map((x) => [`${x.isParent ? 'parent' : 'student'}-${x.id}`, x]))}
            setPersons={(value) => {
              const updatedPersons = Array.from(value.values());

              if (activeTab === "parents") {
                setParentPersons(updatedPersons);
              } else {
                setStudentPersons(updatedPersons);
              }
            }}
            errors={selectedItemsErrors}
            setErrors={setSelectedItemsErrors}
          />
          )}
        </Stack>
      </Page>
    </>
  );
};
